import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, LogIn, LogOut, FolderOpen, Shield } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { useUser } from "@/hooks/useUser";
import { useProAccess } from "@/hooks/useProAccess";
import { supabase } from "@/integrations/supabase/client";

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const { user, loading } = useUser();
  const { isAdmin } = useProAccess();

  const links = [
    { to: "/", label: "Accueil" },
    { to: "/blog", label: "Blog" },
    { to: "/guide-nlpd", label: "Guide gratuit" },
    { to: "/acheter-diagnostic-pro", label: "Dossier Pro nLPD" },
    { to: "/faq", label: "FAQ" },
    { to: "/a-propos", label: "À propos" },
  ];

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setMobileOpen(false);
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border/80 bg-background/85 backdrop-blur-md supports-[backdrop-filter]:bg-background/70">
      <div className="container flex h-16 items-center justify-between">
        <Link
          to="/"
          className="group flex items-center gap-2 font-bold text-xl"
          style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
        >
          <BrandLogo className="h-7 w-7 text-primary transition-transform group-hover:scale-105" />
          <span className="leading-none">
            <span className="text-primary">nLPD</span>
            <span className="text-foreground">&nbsp;Conform</span>
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-7">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="relative text-sm font-medium text-foreground/70 hover:text-primary transition-colors after:pointer-events-none after:absolute after:inset-x-0 after:-bottom-1 after:h-px after:origin-center after:scale-x-0 after:bg-primary after:transition-transform hover:after:scale-x-100"
            >
              {l.label}
            </Link>
          ))}

          {/* Account state — admins see "Admin" instead of "Mes diagnostics"
              (the /audit-pro page is anyway admin-aware now and shows all
              sessions; admins can reach it from /admin/audits or by typing
              /audit-pro). Keeps the desktop menu uncluttered. */}
          {!loading && user ? (
            <div className="flex items-center gap-3 ml-2 pl-4 border-l border-border">
              {isAdmin ? (
                <Link
                  to="/admin"
                  className="inline-flex items-center gap-1 text-sm font-medium text-amber-700 hover:text-amber-900 transition-colors"
                  title="Espace administration"
                >
                  <Shield className="h-4 w-4" />
                  Admin
                </Link>
              ) : (
                <Link
                  to="/audit-pro"
                  className="inline-flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
                  title="Voir mes diagnostics"
                >
                  <FolderOpen className="h-4 w-4" />
                  Mes diagnostics
                </Link>
              )}
              <button
                onClick={handleSignOut}
                className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors"
                title={user.email || "Déconnexion"}
              >
                <LogOut className="h-3.5 w-3.5" />
                Déconnexion
              </button>
            </div>
          ) : !loading ? (
            <Link
              to="/login"
              className="inline-flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-primary transition-colors ml-2 pl-4 border-l border-border"
            >
              <LogIn className="h-4 w-4" />
              Connexion
            </Link>
          ) : null}

          <div className="flex items-center gap-2 ml-2">
            <Button onClick={() => navigate("/")} size="sm">
              Analyser mon site
            </Button>
          </div>
        </nav>

        {/* Mobile toggle */}
        <button
          className="md:hidden p-2"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Menu"
        >
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* Mobile nav */}
      {mobileOpen && (
        <div className="md:hidden border-t bg-card p-4 space-y-3">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              onClick={() => setMobileOpen(false)}
              className="block text-sm font-medium text-muted-foreground hover:text-primary"
            >
              {l.label}
            </Link>
          ))}

          {/* Mobile account state — same admin/user split as desktop. */}
          {!loading && user ? (
            <div className="border-t pt-3 space-y-2">
              {isAdmin ? (
                <Link
                  to="/admin"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-2 text-sm font-medium text-amber-700 hover:text-amber-900"
                >
                  <Shield className="h-4 w-4" />
                  Admin
                </Link>
              ) : (
                <Link
                  to="/audit-pro"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-primary"
                >
                  <FolderOpen className="h-4 w-4" />
                  Mes diagnostics
                </Link>
              )}
              <div className="text-xs text-muted-foreground truncate" title={user.email || ""}>
                {user.email}
              </div>
              <button
                onClick={handleSignOut}
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary"
              >
                <LogOut className="h-4 w-4" />
                Déconnexion
              </button>
            </div>
          ) : !loading ? (
            <Link
              to="/login"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-primary border-t pt-3"
            >
              <LogIn className="h-4 w-4" />
              Connexion
            </Link>
          ) : null}

          <div className="space-y-2 pt-2">
            <Button
              onClick={() => {
                navigate("/");
                setMobileOpen(false);
              }}
              size="sm"
              className="w-full"
            >
              Analyser mon site
            </Button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
